import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { AuthError } from '@supabase/supabase-js';
import { getClient, sessionStorageKey, resolveAuthEnv } from './supabase';
import { type Env } from './env';
import { setCurrentUser as setErrorLoggerUser } from './errorLogger';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'superuser' | 'editor' | 'editor_live' | 'editor_proto' | 'editor_regional';
  mfaEnrolled: boolean;
}

interface AuthContextValue {
  /** The env this session belongs to. Sessions are per-project, so this is
   *  part of the auth state, not a display detail. */
  env: Env;
  user: AdminUser | null;
  loading: boolean;
  mfaRequired: boolean;
  login: (email: string, password: string) => Promise<{ error: string | null }>;
  verifyMfa: (code: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Auth bootstrap rewrite (2026-05-02). Replaces the 10s navigator-locks
 * orphan-recovery dance with a much smaller surface:
 *
 *   1. On mount, check localStorage synchronously for a stored session.
 *      Empty → render login form immediately (no wait, no flicker).
 *      Present → run `getSession()` with an 8s guard so an expired-JWT
 *      token-refresh round-trip has time to complete before we fall
 *      through to login. Earlier 2s guard was too aggressive — refresh
 *      took 2-4s on slow connections, falsely showing login form to
 *      users with valid stored sessions, then auto-logging-them-in via
 *      onAuthStateChange a beat later (worst-of-both-worlds UX).
 *
 *   2. NO poison flag that permanently disables subsequent auth-state
 *      handling. The previous code set `timedOut = true` after the
 *      bootstrap timeout, which then blocked every subsequent
 *      `onAuthStateChange` event for the lifetime of the component —
 *      meaning a successful login after a timed-out bootstrap could
 *      land in a half-rendered state.
 *
 *   3. Admin profile fetch happens inline with session resolution but
 *      is also guarded by the same 2s deadline. If the profile query
 *      hangs, we fall through to "no user" rather than wedging.
 *
 *   4. `onAuthStateChange` is the canonical re-sync path. After login,
 *      logout, or token refresh, it re-fetches the admin profile and
 *      updates the user state. No timeouts here — the user is already
 *      past the gate by the time these fire.
 *
 *   5. mfaRequired stays in the interface for Login.tsx compatibility
 *      but is never set to true. (Written when MFA was dead code. As of
 *      2026-08-21 MFA is live — enforced by RLS on 23 tables, with the
 *      step-up in components/MfaGate.tsx and enrolment in pages/Security.tsx
 *      — and it was built as the separate surface this note recommended, so
 *      the login path is still free of an MFA branch.)
 *
 * Cloudflare Zero Trust still gates who can REACH admin.votd.io. Supabase
 * Auth here exists so RLS policies keyed on auth.uid() resolve to a real
 * admin row. That's the only job — nothing fancier.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // Identity follows the env (2026-08-22). A session issued by one Supabase
  // project is not a credential at another, so switching envs switches WHO you
  // are, not just what you read. AuthProvider sits above EnvProvider — it has to,
  // because /login and /auth/callback render before any env-prefixed route
  // exists — so it resolves the env from the path itself using the same
  // resolver EnvProvider uses. Both therefore always agree.
  const location = useLocation();
  const env = resolveAuthEnv(location.pathname, location.search);
  const supabase = getClient(env);

  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Env changed: the previous project's user is not this project's user.
    // Clear before re-bootstrapping so a stale name/role can never be shown
    // beside the new env's data for the few hundred ms the lookup takes.
    setUser(null);
    setErrorLoggerUser(null);
    setLoading(true);

    if (!supabase) {
      // Supabase not configured — can't authenticate, app runs empty.
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Two guard tiers based on whether localStorage holds a session.
    //
    // - Empty localStorage → 0ms guard equivalent (no race possible). We know
    //   there's nothing to restore, so we set loading=false immediately and
    //   render the login form. No flicker, no wait.
    //
    // - Stored session present → 8s guard. getSession() may need to
    //   round-trip to Supabase to refresh an expired access token (default
    //   JWT expiry is 1h; refresh token good for 7+d). On a slow connection
    //   that round-trip can comfortably take 2-4s. The previous 2s guard
    //   was firing before the refresh completed — falsely showing the login
    //   form to a user who had a perfectly valid stored session, then
    //   silently auto-logging-them-in via onAuthStateChange a beat later.
    //   Worst-of-both-worlds UX. 8s is generous enough for the slowest
    //   plausible mobile/desktop connection without hanging the UI on a
    //   genuinely-dead session.
    //
    // Why localStorage check instead of always-8s: empty-storage users
    // (incognito, brand-new browser, post-logout) shouldn't wait 8s to
    // see the login form. Synchronous storage check has zero latency and
    // tells us definitively whether the wait is justified.
    let hasStoredSession = false;
    try {
      hasStoredSession = window.localStorage.getItem(sessionStorageKey(env)) !== null;
    } catch {
      // localStorage unavailable (private browsing in some browsers, quota
      // exceeded, disabled). Treat as empty — show login form, no wait.
    }

    if (!hasStoredSession) {
      setLoading(false);
      // Still set up onAuthStateChange below in case a magic-link callback
      // populates the session shortly after mount.
    }

    // Defect 4 (2026-05-18) — when localStorage indicates a stored session
    // but the initial getSession() round-trip is slow (>8s), retry ONCE with
    // a longer 15s budget before falling through to the login form. The
    // previous external guard timer would dump the editor straight to login
    // after 8s, then onAuthStateChange would silently re-auth them a beat
    // later — a confusing half-rendered state. With the retry, we either
    // restore the session successfully or surface explicit failure rather
    // than silently bouncing the user.
    //
    // When hasStoredSession is false, no retry — empty storage means there's
    // nothing to restore and we should show the login form immediately.
    const BOOTSTRAP_FIRST_BUDGET_MS = 8_000;
    const BOOTSTRAP_RETRY_BUDGET_MS = 15_000;

    const raceWithTimeout = <T,>(p: PromiseLike<T>, budgetMs: number): Promise<T> =>
      Promise.race([
        Promise.resolve(p),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('bootstrap_timeout')), budgetMs)
        ),
      ]);

    (async () => {
      try {
        let result: Awaited<ReturnType<typeof supabase.auth.getSession>>;
        try {
          result = await raceWithTimeout(supabase.auth.getSession(), BOOTSTRAP_FIRST_BUDGET_MS);
        } catch (firstErr) {
          if ((firstErr as Error).message === 'bootstrap_timeout' && hasStoredSession) {
            console.debug('[AuthProvider] initial getSession timed out at 8s, retrying once at 15s');
            result = await raceWithTimeout(supabase.auth.getSession(), BOOTSTRAP_RETRY_BUDGET_MS);
          } else {
            throw firstErr;
          }
        }
        if (cancelled) return;

        const { data: { session } } = result;
        if (session?.user) {
          await syncAdminProfile(session.user.id);
        }
      } catch {
        // Both attempts failed (or no stored session and first failed) —
        // fall through to "no user" state. Editor sees the login form.
      } finally {
        if (!cancelled) {
          cancelled = true;
          setLoading(false);
        }
      }
    })();

    // Re-sync on auth state changes (login, logout, token refresh). NO timeout
    // poison flag — these can fire any time and must always update state.
    //
    // CRITICAL — the callback must NOT be async and must NOT await a Supabase
    // call. gotrue-js invokes onAuthStateChange callbacks while holding its
    // internal auth lock; a supabase.from().select() inside the callback needs
    // the auth token, which re-acquires that lock → deadlock. The FIRST login
    // slips through but leaves the lock queue wedged, so the SECOND login (or
    // the next token refresh) hangs forever on the client — the request reaches
    // Supabase and returns 200, but the client promise never resolves and the
    // login times out. Deferring the profile fetch with setTimeout(0) runs it
    // outside the lock and breaks the deadlock. Per supabase-js docs: never
    // await Supabase calls directly inside an onAuthStateChange callback.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setTimeout(() => { void syncAdminProfile(session.user.id); }, 0);
      } else {
        setUser(null);
        setErrorLoggerUser(null);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };

    async function syncAdminProfile(userId: string) {
      try {
        // api_admin.whoami(): the caller's own admin row or none. It is the one door that answers
        // at aal1, because this runs before MfaGate asks for the authenticator code (CS-5).
        void userId;
        const { data } = await supabase!
          .schema('api_admin')
          .rpc('whoami')
          .single<{ id: string; email: string; full_name: string; role: AdminUser['role']; mfa_enrolled: boolean; is_active: boolean }>();

        if (data && data.is_active) {
          setUser({
            id:          data.id,
            email:       data.email,
            name:        data.full_name,
            role:        data.role,
            mfaEnrolled: data.mfa_enrolled,
          });
          // Surface the user id to the global error logger so future error
          // reports carry user attribution.
          setErrorLoggerUser(data.id);
        } else {
          // Authenticated to Supabase but no matching admin_users row (or
          // inactive) — sign out to avoid a half-authenticated state.
          await supabase!.auth.signOut();
          setUser(null);
          setErrorLoggerUser(null);
        }
      } catch {
        // Profile fetch failed — leave existing user state untouched. The
        // user can refresh; if their session is genuinely dead the next
        // data-fetch from a page will surface a 401.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env]);

  const login = async (email: string, password: string): Promise<{ error: string | null }> => {
    if (!supabase) return { error: 'Authentication service not configured.' };

    // Both awaits below are wrapped in Promise.race with explicit timeouts
    // (re-added 2026-05-18 — Defect 3 in the stability rebuild).
    //
    // The previous comment here read "no Promise.race, no timeout layer"
    // on the theory that the navigator.locks orphan (now neutralised by the
    // no-op lock override in supabase.ts) was the only thing that ever made
    // these awaits hang. That theory was wrong. Independent causes that
    // can still hang the await with no error:
    //   - slow / cold Supabase Auth lambda
    //   - Cloudflare DNS hiccup between admin.votd.io and Supabase
    //   - hung TCP connection (mobile network handoff, ISP routing blip)
    //   - PostgREST gateway cold-start (the same class that motivated the
    //     keep-alive ping in EditorialQueue.tsx)
    // Under any of those the previous code left the Login button frozen on
    // "Signing in..." forever — the editor's only escape was a hard refresh.
    //
    // Budgets: signInWithPassword gets 25s (auth round-trip can legitimately
    // take 2-4s on slow connections); admin_users.select gets 15s (single
    // indexed PK lookup, should be <500ms). Both surface explicit error
    // messages on timeout so the editor can retry rather than refresh.
    const LOGIN_TIMEOUT_MS = 25_000;
    const PROFILE_TIMEOUT_MS = 15_000;

    const signInRes = await Promise.race([
      supabase.auth.signInWithPassword({ email, password }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), LOGIN_TIMEOUT_MS)
      ),
    ]).catch((err: Error) => {
      if (err.message === 'timeout') {
        return { __timedOut: true } as const;
      }
      return { __error: err.message } as const;
    });

    if ('__timedOut' in signInRes) {
      return { error: 'Login took too long — the connection may be unreliable. Please try again.' };
    }
    if ('__error' in signInRes) {
      return { error: `Login failed: ${signInRes.__error}` };
    }
    const { data, error } = signInRes;
    if (error) return { error: friendlyError(error) };
    if (!data.session) return { error: 'Login failed. Please try again.' };

    // Fetch admin profile inline. onAuthStateChange will ALSO fire and
    // re-sync, but doing it inline here means the user state is set before
    // login() returns — so the caller's redirect happens against fresh state.
    const profileRes = await Promise.race([
      supabase
        .schema('api_admin')
        .rpc('whoami')
        .single<{ id: string; email: string; full_name: string; role: AdminUser['role']; mfa_enrolled: boolean; is_active: boolean }>(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), PROFILE_TIMEOUT_MS)
      ),
    ]).catch((err: Error) => {
      if (err.message === 'timeout') {
        return { __timedOut: true } as const;
      }
      return { __error: err.message } as const;
    });

    if ('__timedOut' in profileRes) {
      // Don't sign out — the auth session was successfully established;
      // it's only the profile lookup that hung. Surfacing the error lets
      // the editor retry without losing their auth state.
      return { error: 'Profile lookup timed out — please try again.' };
    }
    if ('__error' in profileRes) {
      await supabase.auth.signOut();
      return { error: `Profile lookup failed: ${profileRes.__error}` };
    }
    const { data: profile, error: profileError } = profileRes;

    if (profileError || !profile) {
      await supabase.auth.signOut();
      return { error: 'Account not found or inactive. Contact your administrator.' };
    }
    if (!profile.is_active) {
      await supabase.auth.signOut();
      return { error: 'Account is inactive. Contact your administrator.' };
    }

    setUser({
      id:          profile.id,
      email:       profile.email,
      name:        profile.full_name,
      role:        profile.role,
      mfaEnrolled: profile.mfa_enrolled,
    });

    return { error: null };
  };

  // MFA is NO LONGER DEAD CODE (2026-08-21) — but this function is still not the
  // thing doing it, so the comment is corrected rather than deleted.
  //
  // Enforcement now lives in the database: a restrictive RLS policy on all 23
  // admin-gated tables requires aal2 from any admin who has a verified factor.
  // The step-up UI is components/MfaGate.tsx, which wraps the authenticated shell
  // in AdminLayout, and enrolment is pages/Security.tsx.
  //
  // That is exactly the shape the previous note here recommended — "a separate
  // post-login route, not a branch in the critical login path" — so `mfaRequired`
  // still never flips to true and the login path is still untouched. This helper
  // remains for the Login.tsx interface only; MfaGate does its own challenge and
  // verify against the live factor list rather than calling through here.
  const verifyMfa = async (code: string): Promise<{ error: string | null }> => {
    if (!supabase) return { error: 'Authentication service not configured.' };
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const totp = factors?.totp?.[0];
    if (!totp) return { error: 'No authenticator enrolled.' };
    const { data: challenge } = await supabase.auth.mfa.challenge({ factorId: totp.id });
    if (!challenge) return { error: 'Could not start MFA challenge.' };
    const { error } = await supabase.auth.mfa.verify({
      factorId: totp.id,
      challengeId: challenge.id,
      code,
    });
    if (error) return { error: 'Invalid code. Try again.' };
    return { error: null };
  };

  const logout = async () => {
    if (supabase) await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ env, user, loading, mfaRequired: false, login, verifyMfa, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

function friendlyError(error: AuthError): string {
  if (error.message.includes('Invalid login credentials')) return 'Invalid email or password.';
  if (error.message.includes('Email not confirmed')) return 'Please confirm your email first.';
  if (error.message.includes('Too many requests')) return 'Too many attempts. Please wait a moment.';
  return error.message;
}
